// MED-1319 Calendar file (.ics, RFC 5545) for a booked session. A visitor
// who is not logged in has no account page, so the done screen offers this
// file as the way to keep the session.

export interface BookingIcsInput {
  scheduledStart: string;
  durationMinutes: number;
  title: string;
  coachName: string;
  videoLink: string | null;
  /** The time the file is made. Tests pass a fixed value. */
  now?: Date;
}

/** Format an instant as "YYYYMMDDTHHMMSSZ" in UTC. */
function formatUtc(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/** Escape a text value: backslash, semicolon, comma, and new lines. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/**
 * Fold a line to 75 octets or fewer. Each next part starts with a space.
 * The split never cuts a multi-byte character.
 */
function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const parts: string[] = [];
  let current = '';
  let limit = 75;
  for (const char of line) {
    if (encoder.encode(current + char).length > limit) {
      parts.push(current);
      current = ' ';
      limit = 75;
    }
    current += char;
  }
  parts.push(current);
  return parts.join('\r\n');
}

export function buildBookingIcs({
  scheduledStart,
  durationMinutes,
  title,
  coachName,
  videoLink,
  now = new Date(),
}: BookingIcsInput): string {
  const start = new Date(scheduledStart);
  const end = new Date(start.getTime() + durationMinutes * 60_000);
  const summary = `${title} with ${coachName}`;
  const description = videoLink
    ? `Join the video meeting: ${videoLink}`
    : 'The video link is in your calendar invite.';

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Medley//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${start.getTime()}-${Math.random().toString(36).slice(2, 10)}@medley`,
    `DTSTAMP:${formatUtc(now)}`,
    `DTSTART:${formatUtc(start)}`,
    `DTEND:${formatUtc(end)}`,
    `SUMMARY:${escapeText(summary)}`,
    `DESCRIPTION:${escapeText(description)}`,
    ...(videoLink ? [`LOCATION:${escapeText(videoLink)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return lines.map(foldLine).join('\r\n') + '\r\n';
}

/** Start a download of the calendar file in the browser. */
export function downloadBookingIcs(input: BookingIcsInput): void {
  const blob = new Blob([buildBookingIcs(input)], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'medley-session.ics';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
